const HEATMAP_REGION_LABELS = {
  shoulders: "어깨(전체)",
  front_delts: "전면 어깨",
  side_delts: "측면 어깨",
  rear_delts: "후면 어깨",
  chest: "가슴",
  upper_chest: "윗가슴",
  lower_chest: "아랫가슴",
  biceps: "이두",
  triceps: "삼두",
  quads: "대퇴사두",
  calves: "종아리",
  traps: "상부 승모",
  mid_traps: "중부 승모",
  upper_back: "상부 등(능형/대원)",
  back: "등(전체)",
  lats: "광배근",
  lower_back: "기립근(하부)",
  glutes: "둔근",
  hamstrings: "햄스트링",
  abs: "복근",
  forearms: "전완근",
};

const MUSCLE_LABELS = {
  chest: "가슴",
  back: "등",
  traps: "승모",
  biceps: "이두",
  brachialis: "상완근",
  triceps: "삼두",
  shoulders: "어깨",
  shoulder: "어깨",
  rear_delts: "후면 어깨",
  front_delts: "전면 어깨",
  side_delts: "측면 어깨",
  quads: "대퇴사두",
  glutes: "둔근",
  hamstrings: "햄스트링",
  calves: "종아리",
  abs: "복근",
  forearms: "전완",
  lats: "광배",
  upper_back: "상부 등",
  lower_back: "하부 등",
  mid_traps: "중부 승모",
  lower_traps: "하부 승모",
  upper_chest: "윗가슴",
  lower_chest: "아랫가슴",
};

const HEATMAP_VIEWS = {
  front: [
    "shoulders",
    "front_delts",
    "side_delts",
    "chest",
    "upper_chest",
    "lower_chest",
    "abs",
    "biceps",
    "forearms",
    "triceps",
    "quads",
    "calves",
  ],
  back: [
    "traps",
    "mid_traps",
    "rear_delts",
    "upper_back",
    "lats",
    "back",
    "lower_back",
    "glutes",
    "hamstrings",
    "calves",
    "triceps",
    "forearms",
  ],
};

const MUSCLE_TO_REGION = {
  chest: "chest",
  upper_chest: "upper_chest",
  lower_chest: "lower_chest",
  back: "back",
  lats: "lats",
  upper_back: "upper_back",
  lower_back: "lower_back",
  traps: "traps",
  mid_traps: "mid_traps",
  biceps: "biceps",
  brachialis: "biceps",
  triceps: "triceps",
  forearms: "forearms",
  shoulders: "shoulders",
  shoulder: "shoulders",
  rear_delts: "rear_delts",
  front_delts: "front_delts",
  side_delts: "side_delts",
  quads: "quads",
  glutes: "glutes",
  hamstrings: "hamstrings",
  calves: "calves",
  abs: "abs",
  core: "abs",
};

const PRIMARY_WEIGHT = 1;
const SECONDARY_WEIGHT = 0.45;

export function formatMuscleLabel(value) {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }

  return MUSCLE_LABELS[raw.toLowerCase()] || raw;
}

function normalizeHeatmapMuscleKey(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) {
    return "";
  }

  return MUSCLE_TO_REGION[raw] || raw;
}

export function createEmptyBodyHeatmap() {
  return {
    totalScore: 0,
    maxScore: 0,
    front: HEATMAP_VIEWS.front.map((key) => ({
      key,
      label: HEATMAP_REGION_LABELS[key] || key,
      score: 0,
      ratio: 0,
    })),
    back: HEATMAP_VIEWS.back.map((key) => ({
      key,
      label: HEATMAP_REGION_LABELS[key] || key,
      score: 0,
      ratio: 0,
    })),
    summary: [],
  };
}

export function createBodyHeatmapFromEntries(entries) {
  const regionScores = {};

  for (const entry of entries || []) {
    if (!entry || !Number.isFinite(entry.sets) || entry.sets <= 0) {
      continue;
    }

    const primaryKey = normalizeHeatmapMuscleKey(entry.primaryMuscle);
    if (primaryKey) {
      regionScores[primaryKey] = (regionScores[primaryKey] || 0) + entry.sets * PRIMARY_WEIGHT;
    }

    const secondaryKeys = Array.from(
      new Set(
        (entry.secondaryMuscles || [])
          .map((muscle) => normalizeHeatmapMuscleKey(muscle))
          .filter((muscleKey) => muscleKey && muscleKey !== primaryKey),
      ),
    );

    secondaryKeys.forEach((muscleKey) => {
      regionScores[muscleKey] = (regionScores[muscleKey] || 0) + entry.sets * SECONDARY_WEIGHT;
    });
  }

  const scoredEntries = Object.entries(regionScores)
    .map(([key, score]) => ({
      key,
      label: HEATMAP_REGION_LABELS[key] || formatMuscleLabel(key),
      score: Math.round(score * 100) / 100,
    }))
    .filter((entry) => entry.score > 0);

  if (scoredEntries.length === 0) {
    return createEmptyBodyHeatmap();
  }

  const totalScore = scoredEntries.reduce((sum, entry) => sum + entry.score, 0);
  const maxScore = scoredEntries.reduce((max, entry) => Math.max(max, entry.score), 0);
  const scoreMap = scoredEntries.reduce((acc, entry) => {
    acc[entry.key] = entry.score;
    return acc;
  }, {});

  const buildView = (viewKey) =>
    HEATMAP_VIEWS[viewKey].map((key) => {
      const score = scoreMap[key] || 0;
      return {
        key,
        label: HEATMAP_REGION_LABELS[key] || formatMuscleLabel(key),
        score,
        ratio: maxScore > 0 ? score / maxScore : 0,
      };
    });

  const summary = scoredEntries
    .sort((a, b) => b.score - a.score)
    .slice(0, 4)
    .map((entry) => ({
      ...entry,
      share: totalScore > 0 ? Math.round((entry.score / totalScore) * 100) : 0,
      ratio: maxScore > 0 ? entry.score / maxScore : 0,
    }));

  return {
    totalScore,
    maxScore,
    front: buildView("front"),
    back: buildView("back"),
    summary,
  };
}
